import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingBag,
  Minus,
  Plus,
  Trash2,
  ArrowRight,
  Tag,
  Truck,
  ChevronRight,
  X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import ProductCard from '../../components/common/ProductCard';
import { useCart } from '../../context/CartContext';
import { products } from '../../data/mock';

const CartPage = () => {
  const {
    items,
    subtotal,
    shipping,
    discountAmount,
    promoCode,
    promoDiscount,
    total,
    updateQuantity,
    removeItem,
    applyPromoCode,
    removePromoCode,
  } = useCart();

  const [promoInput, setPromoInput] = React.useState('');
  const [promoError, setPromoError] = React.useState('');

  const handleApplyPromo = () => {
    if (applyPromoCode(promoInput)) {
      setPromoInput('');
      setPromoError('');
    } else {
      setPromoError('Invalid promo code');
    }
  };

  // Get suggested products not in cart
  const suggestedProducts = products
    .filter((p) => !items.some((item) => item.productId === p.id))
    .slice(0, 4);

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
          <p className="text-gray-500 mb-6">Discover clinically-guided rehab products</p>
          <Link to="/shop">
            <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8">
              Start Shopping
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Shopping Cart ({items.reduce((sum, item) => sum + item.quantity, 0)} items)
          </h1>
          <Link to="/shop" className="text-red-600 hover:text-red-700 font-medium text-sm flex items-center gap-1">
            Continue Shopping <ChevronRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => (
              <Card key={item.productId} className="border-gray-100">
                <CardContent className="p-4 md:p-6">
                  <div className="flex gap-4">
                    {/* Product Image */}
                    <Link to={`/product/${item.productId}`} className="flex-shrink-0">
                      <div className="w-24 h-24 md:w-32 md:h-32 bg-gray-100 rounded-xl overflow-hidden">
                        <img
                          src={item.product?.images?.[0] || 'https://placehold.co/128x128'}
                          alt={item.product?.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </Link>

                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <Link to={`/product/${item.productId}`}>
                            <h3 className="font-semibold text-gray-900 hover:text-red-600 transition-colors line-clamp-2">
                              {item.product?.name}
                            </h3>
                          </Link>
                          <p className="text-sm text-gray-500 mt-1 line-clamp-1">
                            {item.product?.shortDescription}
                          </p>
                        </div>
                        <button
                          onClick={() => removeItem(item.productId)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Price & Quantity */}
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center border border-gray-200 rounded-lg">
                          <button
                            onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-l-lg transition-colors"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="w-10 text-center font-medium text-gray-900">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-50 rounded-r-lg transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>

                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">
                            ${(item.product?.price * item.quantity).toFixed(2)}
                          </p>
                          {item.quantity > 1 && (
                            <p className="text-sm text-gray-500">
                              ${item.product?.price.toFixed(2)} each
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="border-gray-100 sticky top-24">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Order Summary</h2>

                {/* Promo Code */}
                <div className="mb-6">
                  {promoCode ? (
                    <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Tag className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-700">
                          {promoCode} (-{promoDiscount}%)
                        </span>
                      </div>
                      <button
                        onClick={removePromoCode}
                        className="text-emerald-600 hover:text-emerald-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={promoInput}
                          onChange={(e) => setPromoInput(e.target.value)}
                          placeholder="Promo code"
                          className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                        />
                        <Button
                          variant="outline"
                          onClick={handleApplyPromo}
                          className="border-gray-200"
                        >
                          Apply
                        </Button>
                      </div>
                      {promoError && (
                        <p className="text-sm text-red-600 mt-1">{promoError}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">Try: REHAB10, PHYSIO15, WELCOME20</p>
                    </div>
                  )}
                </div>

                {/* Summary Lines */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium text-gray-900">${subtotal.toFixed(2)}</span>
                  </div>
                  {discountAmount > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-emerald-600">Discount ({promoDiscount}%)</span>
                      <span className="font-medium text-emerald-600">-${discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-medium text-gray-900">
                      {shipping === 0 ? (
                        <span className="text-emerald-600">FREE</span>
                      ) : (
                        `$${shipping.toFixed(2)}`
                      )}
                    </span>
                  </div>
                  {shipping > 0 && (
                    <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
                      <Truck className="w-4 h-4 text-amber-600" />
                      <span className="text-sm text-amber-800">
                        Add ${(75 - subtotal).toFixed(2)} more for free shipping!
                      </span>
                    </div>
                  )}
                </div>

                {/* Total */}
                <div className="flex items-center justify-between py-4 border-t border-gray-100">
                  <span className="text-lg font-bold text-gray-900">Total</span>
                  <span className="text-2xl font-bold text-gray-900">${total.toFixed(2)}</span>
                </div>

                {/* Checkout Button */}
                <Link to="/checkout">
                  <Button className="w-full bg-red-600 hover:bg-red-700 text-white rounded-full h-12 shadow-lg shadow-red-200 hover:shadow-xl transition-all">
                    Proceed to Checkout
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>

                {/* Trust Indicators */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
                    <span>🔒 Secure checkout</span>
                    <span>•</span>
                    <span>30-day returns</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Suggested Products */}
        {suggestedProducts.length > 0 && (
          <section className="mt-16">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Frequently Bought Together</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {suggestedProducts.map((product) => (
                <ProductCard key={product.id} product={product} compact />
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Footer Spacer for mobile nav */}
      <div className="h-20 md:h-0" />
    </div>
  );
};

export default CartPage;